import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Img from 'react-image';
import get from 'lodash.get';
import hash from 'object-hash';
import {
  Flex,
  Typography,
  SelectDropdown,
  Box,
  Overlay,
  Loader,
  Card,
  CardContent,
  CardTitle,
  Button,
  color
} from '@kistech/kis-ui';
import NoImagePreview from 'assets/no_image_preview.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as clientSelectors } from 'store/clients';
import { actions as configurationsActions } from 'store/configurations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { getSortedList } from 'utils/stringFormat';
import { ImageUploadOverlay } from '../Shared/ImageUploadOverlay';
import { MediaRow } from '../Shared/MediaRow';
import { MediaType, MediaResponse, ConfigurationMediaProps } from '../../types';

const initialImage = {
  exists: false,
  loading: false,
  width: 0,
  height: 0
};

const OtherImages: React.FC<ConfigurationMediaProps> = (props: ConfigurationMediaProps) => {
  const { id, configuration, ticketing } = props.configuration;

  const { pending, successful } = useSelector(mediaSelectors.selectAction);
  const otherImages = useSelector(mediaSelectors.selectOtherImages);
  const clientId = useSelector(clientSelectors.selectClientId);
  const [updateConfiguration, getMedia, resetAction, deleteMedia] = useActions([
    configurationsActions.updateConfiguration,
    mediaActions.getMedia,
    mediaActions.resetAction,
    mediaActions.deleteMedia
  ]);

  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<MediaResponse | null>(null);
  const [imageProperties, setImageProperties] = useState<typeof initialImage>(initialImage);
  const [previewUrl, setPreviewUrl] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showImageUploadOverlay, setShowImageUploadOverlay] = useState<boolean>(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState<string | null>(null);

  const promoCodeCustomOptions = get(ticketing, 'promoCode.selection.customOptions', []);
  const promoCodeManualEntry = get(ticketing, 'promoCode.selection.manualEntry.enabled', false);

  // make an array contains customOptions and Manual entry promocode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const promoCodesList: string[] = promoCodeCustomOptions.map((customOption: any) => customOption.code);
  if (promoCodeManualEntry) promoCodesList.push('Manual Entry');

  const loading = imageProperties.loading || pending;

  // getMedia if configuration or type changes
  React.useEffect(() => {
    if (id && mediaType && mediaType.value) {
      // reset type dropdown if background is selected, and flowTemplate is not fair
      const { flowTemplate } = configuration;
      if (flowTemplate !== 'fair' && mediaType.value === 'background-image') {
        setMediaType(null);
      } else {
        getMedia({ type: mediaType.value, configurationId: id });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType, id]);

  // getMedia and updateConfig if success (upload/delete)
  React.useEffect(() => {
    if (successful && id && mediaType && mediaType.value) {
      getMedia({ type: mediaType.value, configurationId: id });
      // updateConfig with nothing so the kiosk(s) fetch the updated images
      updateConfiguration({ id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  // set the selected image if found
  React.useEffect(() => {
    if (mediaType && mediaType.value && otherImages) {
      const { inactive, splash, logo, logoLarge, background, promoCode } = otherImages;
      if (mediaType.value === 'inactive-image' && inactive && inactive.length > 0) {
        setSelectedMedia(inactive[0]);
      } else if (mediaType.value === 'splash-image' && splash && splash.length > 0) {
        setSelectedMedia(splash[0]);
      } else if (mediaType.value === 'promo-code-image' && promoCode && promoCode.length > 0) {
        if (promoCodesList.length > 0) {
          const imgObj = promoCode.find((item: MediaResponse) => item.name === hash(promoCodesList[0]));
          setSelectedMedia(imgObj || null);
          setSelectedPromoCode(promoCodesList[0]);
        }
      } else if (mediaType.value === 'logo-image' && logo && logo.length > 0) {
        setSelectedMedia(logo[0]);
      } else if (mediaType.value === 'logo-image-large' && logoLarge && logoLarge.length > 0) {
        setSelectedMedia(logoLarge[0]);
      } else if (mediaType.value === 'background-image' && background && background.length > 0) {
        setSelectedMedia(background[0]);
      } else {
        setSelectedMedia(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType, otherImages]);

  // set the preview if selected image changes
  React.useEffect(() => {
    if (selectedMedia && selectedMedia.s3Name && mediaType && mediaType.path) {
      setPreviewUrl(`${process.env.REACT_APP_S3_ASSETS}/${clientId}/${mediaType.path}/${selectedMedia.s3Name}`);
    } else {
      setPreviewUrl('');
    }
    resetAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMedia]);

  // set the image properties if preview changes
  React.useEffect(() => {
    if (previewUrl) {
      getImageProperties();
    } else {
      setImageProperties({ ...imageProperties, exists: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  // options for the type dropdown
  const getMediaTypeList = () => {
    const mediaTypeList: MediaType[] = [
      { name: 'Inactive Page Image', value: 'inactive-image', path: 'inactive_images' },
      { name: 'Splash Page Image', value: 'splash-image', path: 'splash_images' },
      { name: 'Client Logo Image - Small', value: 'logo-image', path: 'logo_images' },
      { name: 'Client Logo Image - Large', value: 'logo-image-large', path: 'logo_images' },
      { name: 'Promo Code Images', value: 'promo-code-image', path: 'promo_code_images' }
    ];
    if (configuration.flowTemplate && configuration.flowTemplate === 'fair') {
      mediaTypeList.push({ name: 'Background Image', value: 'background-image', path: 'background_images' });
    }

    return getSortedList(mediaTypeList, 'name');
  };

  const getImageProperties = () => {
    setImageProperties({ ...imageProperties, loading: true });
    const img = new Image();
    img.src = previewUrl;
    img.onload = () => {
      setImageProperties({
        width: img.width,
        height: img.height,
        loading: false,
        exists: true
      });
    };
    img.onerror = () => setImageProperties({ ...imageProperties, loading: false, exists: false });
  };

  const onDelete = () => {
    if (selectedMedia && selectedMedia.id) {
      deleteMedia(selectedMedia.id);
      setPreviewUrl('');
      resetAction();
    }
    setShowConfirmation(false);
  };

  const promoCodeRowClick = (selectedCode: string) => {
    const { promoCode = [] } = otherImages;
    const promoCodeHashed = hash(selectedCode);
    const imgObj = promoCode.find((item: MediaResponse) => item.name === promoCodeHashed);
    setSelectedMedia(imgObj || null);
    setSelectedPromoCode(selectedCode);
  };

  return (
    <>
      {showConfirmation && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={400}>
              <Typography size={40} weight={600} align="center" style={{ lineHeight: 1 }}>
                Are You Sure?
              </Typography>
            </Box>
          }
          description={
            <Box width={500}>
              <Typography size={24} align="center">
                {'Are you sure you want to delete this image?'}
              </Typography>
            </Box>
          }
          confirmText="Yes"
          onConfirm={onDelete}
          appearance="error"
          cancelText="Cancel"
          onCancel={() => setShowConfirmation(false)}
          height={300}
        />
      )}
      {pending && (
        <Overlay>
          <Loader color={color.BLACK} />
        </Overlay>
      )}
      <Flex direction="column" noWrap>
        <Flex align="center" margin="0 0 6" direction="row">
          <Typography size={24} weight={700} style={{ marginRight: '123px' }}>
            Type
          </Typography>
          <SelectDropdown
            items={getMediaTypeList()}
            itemToString={(item: MediaType) => (item ? item.name : '')}
            selectedItem={mediaType as MediaType}
            onChange={(selectedType) => setMediaType(selectedType)}
            placeholder="Select a type"
          />
        </Flex>
        {mediaType && (
          <>
            {mediaType.value === 'promo-code-image' ? (
              <Flex fullWidth direction="row">
                <Box width={650}>
                  <Card raised hasBorderRadius>
                    <Card.Title
                      title={
                        <Typography size={18} weight={700}>
                          {mediaType.name}
                        </Typography>
                      }
                      gutter={5}
                    />
                    <Flex direction="column" fullWidth>
                      {promoCodesList.length > 0 &&
                        promoCodesList.map((code: string, idx: number) => (
                          <MediaRow
                            key={idx}
                            label={code}
                            selected={selectedPromoCode === code ? true : false}
                            onClick={() => promoCodeRowClick(code)}
                          />
                        ))}
                    </Flex>
                  </Card>
                </Box>
                <Box margin="0 3" width={325}>
                  <Card raised hasBorderRadius>
                    <CardTitle
                      title={
                        <Typography size={18} weight={600}>
                          Image Preview
                        </Typography>
                      }
                      gutter={5}
                    />
                    <CardContent>
                      <Flex padding="2" align="center" justify="center">
                        <Flex width={200} height={150} align="center" justify="center">
                          {loading ? (
                            <Flex height={252} align="center">
                              <Typography>Fetching image...</Typography>
                            </Flex>
                          ) : (
                            <Img
                              src={previewUrl}
                              width="100%"
                              height="100%"
                              style={{ objectFit: 'cover' }}
                              unloader={<img alt="preview" src={NoImagePreview} width={180} height={158} />}
                            />
                          )}
                        </Flex>
                        <Box height={20} fullWidth />
                        {imageProperties.exists ? (
                          <>
                            <Button
                              size="small"
                              appearance="primary"
                              width={250}
                              isLoading={loading}
                              onClick={() => setShowImageUploadOverlay(true)}
                            >
                              Select Different Image
                            </Button>
                            <Box fullWidth margin="2 0" />
                            <Button
                              size="small"
                              width={250}
                              appearance="white"
                              onClick={() => setShowConfirmation(true)}
                              isLoading={loading}
                              data-testid="delete-video-button"
                            >
                              Delete
                            </Button>
                            <Flex fullWidth>
                              <Box width="100%" padding="4 4 0">
                                <Box margin="0 0 2">
                                  <Typography size={18}>File Details</Typography>
                                </Box>
                                {loading ? (
                                  <Typography>Fetching details...</Typography>
                                ) : (
                                  <>
                                    {selectedMedia && (
                                      <Typography size={14}>Name: {selectedMedia.imageName}</Typography>
                                    )}
                                    <Typography size={14}>
                                      Size: {imageProperties.height}px by {imageProperties.width}px
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Flex>
                          </>
                        ) : (
                          <>
                            <Button
                              size="small"
                              appearance="primary"
                              width={250}
                              isLoading={loading}
                              onClick={() => setShowImageUploadOverlay(true)}
                            >
                              Add Image
                            </Button>
                          </>
                        )}
                      </Flex>
                    </CardContent>
                  </Card>
                </Box>
              </Flex>
            ) : (
              <Flex fullWidth direction="row">
                <Box width={650}>
                  <Card raised hasBorderRadius>
                    <Card.Title
                      title={
                        <Typography size={18} weight={700}>
                          {mediaType.name}
                        </Typography>
                      }
                      gutter={5}
                    />
                    {imageProperties.exists ? (
                      <>
                        <Card.Content gutter={5}>
                          <Flex fullWidth>
                            <Box width="45%" padding="0 2 0 0">
                              <Box margin="0 0 2">
                                <Typography size={18}>File Details</Typography>
                              </Box>
                              {loading ? (
                                <Typography>Fetching details...</Typography>
                              ) : (
                                <>
                                  {selectedMedia && <Typography size={14}>Name: {selectedMedia.name}</Typography>}
                                  <Typography size={14}>
                                    Size: {imageProperties.height}px by {imageProperties.width}px
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Flex>
                        </Card.Content>
                      </>
                    ) : (
                      <CardContent>
                        <Box fullWidth margin="0 0 5">
                          <Box margin="0 0 4">
                            <Typography size={18} weight={400}>
                              Add one image
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            width={180}
                            isLoading={loading}
                            onClick={() => setShowImageUploadOverlay(true)}
                          >
                            Add Image
                          </Button>
                        </Box>
                      </CardContent>
                    )}
                  </Card>
                </Box>
                <Box margin="0 3" width={325}>
                  <Card raised hasBorderRadius>
                    <CardTitle
                      title={
                        <Typography size={18} weight={600}>
                          Image Preview
                        </Typography>
                      }
                      gutter={5}
                    />
                    <CardContent>
                      <Flex padding="2" align="center" justify="center">
                        <Flex width={230} height={252} align="center" justify="center">
                          {loading ? (
                            <Flex height={252} align="center">
                              <Typography>Fetching image...</Typography>
                            </Flex>
                          ) : (
                            <Img
                              src={previewUrl}
                              width="100%"
                              height="100%"
                              style={{ objectFit: 'scale-down' }}
                              unloader={<img alt="preview" src={NoImagePreview} width={180} height={158} />}
                            />
                          )}
                        </Flex>
                        <Box height={20} fullWidth />
                        {imageProperties.exists && (
                          <>
                            <Button
                              size="small"
                              appearance="primary"
                              width={250}
                              isLoading={loading}
                              onClick={() => setShowImageUploadOverlay(true)}
                            >
                              Select Different Image
                            </Button>
                            <Box fullWidth margin="2 0" />
                            <Button
                              size="small"
                              width={250}
                              appearance="white"
                              onClick={() => setShowConfirmation(true)}
                              isLoading={loading}
                              data-testid="delete-video-button"
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </Flex>
                    </CardContent>
                  </Card>
                </Box>
              </Flex>
            )}
          </>
        )}
      </Flex>
      {showImageUploadOverlay && (
        <ImageUploadOverlay
          imageType={(mediaType as MediaType).value}
          imageName={selectedPromoCode ? selectedPromoCode : ''}
          configurationId={id}
          onCancel={() => setShowImageUploadOverlay(false)}
          onSuccess={() => setShowImageUploadOverlay(false)}
        />
      )}
    </>
  );
};

export default OtherImages;
